import { useEffect, useRef, useMemo } from 'react'
import * as d3 from 'd3'
import { ZoomIn, ZoomOut, Maximize2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { Entity, Triplet } from '@/types/ingestion'

const PALETTE = [
  '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b',
  '#14b8a6', '#ef4444', '#ec4899', '#6366f1',
]

function hashString(s: string): number {
  let hash = 0
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash)
  }
  return Math.abs(hash)
}

function getTypeColour(entityType: string): string {
  return PALETTE[hashString(entityType) % PALETTE.length]
}

interface SimNode extends d3.SimulationNodeDatum {
  id: string
  label: string
  entityType: string
}

interface SimLink extends d3.SimulationLinkDatum<SimNode> {
  relationship: string
}

interface EntityGraphViewProps {
  entities: Entity[]
  triplets: Triplet[]
}

const WIDTH = 600
const HEIGHT = 400
const NODE_RADIUS = 20

export function EntityGraphView({ entities, triplets }: EntityGraphViewProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const simRef = useRef<d3.Simulation<SimNode, SimLink> | null>(null)
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null)

  const colourMap = useMemo(() => {
    const cm = new Map<string, string>()
    entities.forEach((e) => {
      if (!cm.has(e.entity_type)) cm.set(e.entity_type, getTypeColour(e.entity_type))
    })
    triplets.forEach((t) => {
      if (!cm.has(t.subject_type)) cm.set(t.subject_type, getTypeColour(t.subject_type))
      if (!cm.has(t.object_type)) cm.set(t.object_type, getTypeColour(t.object_type))
    })
    return cm
  }, [entities, triplets])

  useEffect(() => {
    if (!svgRef.current) return
    if (!entities.length && !triplets.length) return

    // Build nodes
    const nodeMap = new Map<string, SimNode>()
    entities.forEach((e) => {
      nodeMap.set(e.name, {
        id: e.name,
        label: e.name,
        entityType: e.entity_type,
      })
    })
    triplets.forEach((t) => {
      if (!nodeMap.has(t.subject_name)) {
        nodeMap.set(t.subject_name, { id: t.subject_name, label: t.subject_name, entityType: t.subject_type })
      }
      if (!nodeMap.has(t.object_name)) {
        nodeMap.set(t.object_name, { id: t.object_name, label: t.object_name, entityType: t.object_type })
      }
    })
    const nodes = Array.from(nodeMap.values())
    const links: SimLink[] = triplets.map((t) => ({
      source: t.subject_name,
      target: t.object_name,
      relationship: t.relationship,
    }))

    // Clear previous
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    // Zoom container
    const g = svg.append('g')

    const zoomBehavior = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 5])
      .on('zoom', (event) => {
        g.attr('transform', event.transform)
      })
    svg.call(zoomBehavior)
    zoomRef.current = zoomBehavior

    // Arrow marker
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', NODE_RADIUS + 10)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#94a3b8')

    // Links
    const linkGroup = g.append('g').attr('class', 'links')
    const linkLine = linkGroup.selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', 1.5)
      .attr('stroke-linecap', 'round')
      .attr('marker-end', 'url(#arrowhead)')

    const linkLabel = linkGroup.selectAll('text')
      .data(links)
      .join('text')
      .attr('text-anchor', 'middle')
      .attr('font-size', 8)
      .attr('fill', '#94a3b8')
      .attr('dy', -4)
      .text((d) => d.relationship)

    // Nodes
    const nodeGroup = g.append('g').attr('class', 'nodes')
    const nodeG = nodeGroup.selectAll('g')
      .data(nodes)
      .join('g')
      .attr('cursor', 'grab')

    nodeG.append('circle')
      .attr('r', NODE_RADIUS)
      .attr('fill', (d) => getTypeColour(d.entityType))
      .attr('stroke', 'white')
      .attr('stroke-width', 2)

    nodeG.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', NODE_RADIUS + 14)
      .attr('font-size', 9)
      .attr('fill', 'currentColor')
      .text((d) => d.label.length > 16 ? d.label.slice(0, 14) + '…' : d.label)

    // Simulation
    const simulation = d3.forceSimulation<SimNode, SimLink>(nodes)
      .force('link', d3.forceLink<SimNode, SimLink>(links).id((d) => d.id).distance(120))
      .force('charge', d3.forceManyBody<SimNode>().strength(-300))
      .force('center', d3.forceCenter(WIDTH / 2, HEIGHT / 2))
      .force('collision', d3.forceCollide<SimNode>().radius(NODE_RADIUS + 10))
      .alpha(0.8)
      .alphaDecay(0.02)

    simRef.current = simulation

    simulation.on('tick', () => {
      linkLine
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y)

      linkLabel
        .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
        .attr('y', (d: any) => (d.source.y + d.target.y) / 2)

      nodeG.attr('transform', (d: any) => `translate(${d.x},${d.y})`)
    })

    // Drag
    const drag = d3.drag<SVGGElement, SimNode>()
      .on('start', (event, d) => {
        if (!event.active) simulation.alphaTarget(0.3).restart()
        d.fx = d.x
        d.fy = d.y
        d3.select(event.sourceEvent.target.closest('g')).attr('cursor', 'grabbing')
      })
      .on('drag', (event, d) => {
        d.fx = event.x
        d.fy = event.y
      })
      .on('end', (event, d) => {
        if (!event.active) simulation.alphaTarget(0)
        d.fx = null
        d.fy = null
        d3.select(event.sourceEvent.target.closest('g')).attr('cursor', 'grab')
      })

    nodeG.call(drag as any)

    return () => {
      simulation.stop()
      simRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, triplets])

  const handleZoomIn = () => {
    if (!svgRef.current || !zoomRef.current) return
    d3.select(svgRef.current).transition().duration(300).call(zoomRef.current.scaleBy, 1.4)
  }

  const handleZoomOut = () => {
    if (!svgRef.current || !zoomRef.current) return
    d3.select(svgRef.current).transition().duration(300).call(zoomRef.current.scaleBy, 0.7)
  }

  const handleFitView = () => {
    if (!svgRef.current || !zoomRef.current) return
    d3.select(svgRef.current)
      .transition()
      .duration(500)
      .call(zoomRef.current.transform, d3.zoomIdentity.translate(0, 0).scale(1))
  }

  if ((!entities || entities.length === 0) && (!triplets || triplets.length === 0)) {
    return (
      <div className="py-12 text-center text-muted-foreground text-sm">
        No graph data available
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Legend + zoom controls */}
      <div className="flex items-center justify-between">
        {colourMap.size > 0 && (
          <div className="flex flex-wrap gap-3">
            {Array.from(colourMap.entries()).map(([type, colour]) => (
              <div key={type} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: colour }} />
                <span className="text-xs text-muted-foreground">{type}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleZoomIn} title="Zoom in">
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleZoomOut} title="Zoom out">
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleFitView} title="Reset view">
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* SVG Graph — d3 manages DOM inside */}
      <div className="rounded-lg border bg-muted/30 overflow-hidden">
        <svg
          ref={svgRef}
          width="100%"
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="block"
          style={{ minHeight: 320 }}
        />
      </div>

      {/* Hint */}
      <p className="text-[11px] text-muted-foreground text-center">
        Scroll to zoom · Drag background to pan · Drag nodes to rearrange
      </p>

      {/* Triplets table */}
      {triplets.length > 0 && (
        <div className="rounded-md border overflow-auto max-h-[200px]">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Subject</th>
                <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Relationship</th>
                <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Object</th>
                <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">Types</th>
              </tr>
            </thead>
            <tbody>
              {triplets.map((t) => (
                <tr key={t.id} className="border-t">
                  <td className="px-2 py-1.5 font-medium">{t.subject_name}</td>
                  <td className="px-2 py-1.5">
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                      {t.relationship}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 font-medium">{t.object_name}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {t.subject_type} → {t.object_type}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
